import { driveItemInfoType } from "../utils/classInfos";
import { globalGraphClient, getSessionForAccessInfo } from "./GraphMain";
import { getFileInFileListCount, openInNewTab } from "../utils/helper";
import store from "./StoreRoot";

const CHUNK_SIZE = 200000

export async function createFolder(parentDriveItemInfo: any, folderName: string) {
  if (!folderName) return;

  // get the parent folder url
  let parentFolderUrl = parentDriveItemInfo ? parentDriveItemInfo.fileUrl : "/me/drive/root";

  // create the folder
  const driveItem = {
    name: folderName,
    folder: { },
    '@microsoft.graph.conflictBehavior': 'rename'
  };

  const res = await globalGraphClient.api(parentFolderUrl+"/children")
    .post(driveItem);

  return res
}

export async function uploadFolderItem(parentDriveItemInfo, file, fileName = "upload.xlsx") {
  if (!file) return;

  let parentFolderUrl = parentDriveItemInfo ? parentDriveItemInfo.fileUrl : "/me/drive/root";

  let newFileName = fileName;
  try {
    // is there already such a file
    const tempFiles = await globalGraphClient
      .api(parentFolderUrl+"/children")
      .get();

    let doubletCount = getFileInFileListCount(tempFiles.value, newFileName);
    if (doubletCount > 0) {
      var i = 0;
      do {
        i += 1;
        if (i > 100) return;
        newFileName = fileName.replace(".xlsx", "-"+i+".xlsx");
        doubletCount = getFileInFileListCount(tempFiles.value, newFileName);
      } while (doubletCount > 0);
    }

    const stream = file.chunkBlob;
    const res = await globalGraphClient.api(parentFolderUrl+":/"+newFileName+":/content")
      .put(stream);
    return res;
  } catch (err) {
    console.log("ERROR uploadFolderItem", err);
  }
}

export async function uploadLargeFile(fileData, uploadInfo, destinationDirectory) {
  if (uploadInfo.chunkIndex === 0) {
    let parentFolderUrl = destinationDirectory.dataItem?.driveItemInfo?.fileUrl || "/me/drive/root";
    const uploadSession = await globalGraphClient.api(`${parentFolderUrl}:/${fileData.name}:/createUploadSession`).post({
      item: {
        "@microsoft.graph.conflictBehavior": "rename",
        "name": fileData.name
      }
    });
    uploadInfo.customData.uploadSession = uploadSession;
    console.log(uploadSession)
  }

  await globalGraphClient.api(uploadInfo.customData.uploadSession.uploadUrl).headers({
    "Content-Length": CHUNK_SIZE,
    "Content-Range": `bytes ${CHUNK_SIZE * uploadInfo.chunkIndex}-${Math.min(CHUNK_SIZE * (uploadInfo.chunkIndex + 1), fileData.size) - 1}/${fileData.size}`,
    "Content-Type": "application/octet-stream"
  }).put(uploadInfo.chunkBlob);
}

export async function downloadDocumentItem(accessInfo) {
  if (!accessInfo) return;
  const {client, fileUrl} = await getSessionForAccessInfo(accessInfo);
  if (!fileUrl || !client) return;

  try {
    let res = await client.api(fileUrl)
      .get()
      .catch(err => console.log("err", err));

    if (res) {
      const downloadFile = res["@microsoft.graph.downloadUrl"] ?? res["webUrl"];
      openInNewTab(downloadFile);
    }
  } catch (err) {
    console.log("ERROR downloadDocumentItem", err)
  }
}

export async function getFolderItems(pathInfo: any) {
  let items = [];
  if (!pathInfo) return items;

  let folderItems;

  let folderId = pathInfo.dataItem?.id;
  let fileUrl = pathInfo.dataItem?.driveItemInfo?.fileUrl;

  if (pathInfo.key === "onedrive") {
    folderId = "OneDrive";
  }
  if (pathInfo.key === "sharepoint") {
    folderId = "SharePoint";
  }
  if (pathInfo.key === "sharedwithme") {
    folderId = "SharedWithMe";
  }
  if (pathInfo.key === "excelhistory") {
    folderId = "ExcelHistory";
  }

  if (folderId === "OneDrive") {
    // it is the main folder
    folderItems = await globalGraphClient.api("/me/drive/root/children")
      .get();
  } else if (folderId === "SharePoint") {
    let sharePointDrive = await globalGraphClient.api("/sites/root/drive")
      .get()
      .catch((err) => {
        console.log("ERROR no access to sites/root", err);
      });
    if (sharePointDrive) {
      const sharePointDriveId = sharePointDrive.id;
      folderItems = await globalGraphClient.api("/drives/"+sharePointDriveId+"/root/children")
        .get();

      if (!folderItems) {
        folderItems = await globalGraphClient.api("/me/followedSites")
        .get()
        console.log("INFO followedSites", folderItems);
      }
    }
  } else if (folderId === "SharedWithMe") {
    folderItems = await globalGraphClient
      .api("me/drive/sharedWithMe")
      .get();
  } else if (folderId === "ExcelHistory") {
    folderItems = await globalGraphClient
    .api("/me/drive/recent")
    .get();
  } else {
    if (folderId) {
      let url = fileUrl ?? "/me/drive/items/"+folderId;
      folderItems = await globalGraphClient.api(url+"/children")
        .get();
    }
  }
  if (!folderItems) { return items }

  const tempFiles = folderItems.value.filter((obj: any) => {
    return obj.name.toLowerCase().includes(".xlsx") || (obj.hasOwnProperty("folder")) || (obj["driveType"] === "documentLibrary");
  });

  tempFiles.forEach((file) => {
    const isFolder = (file.hasOwnProperty("folder") || file["driveType"] === "documentLibrary");
    const driveId = (file.hasOwnProperty("remoteItem")) ? file.remoteItem.parentReference.driveId : file.parentReference.driveId;
    const fileId = file.id;
    const fileUrl = file.webUrl;
    const creatorEmail = file.createdBy.user.email;

    let driveItemInfo = {
      driveItemId: fileId,
      driveItemType: "",
      fileName: file.name,
      fileUrl: "/drives/"+driveId+"/items/"+fileId,
      fileWebUrl: fileUrl,
      shareType: "owner",
      creatorEmail: creatorEmail,
    }

    let tempFile = {
      id: file.id,
      name: file.name,
      size: file.size,
      dateModified: file.lastModifiedDateTime,
      driveItemInfo: driveItemInfo,
      isDirectory: isFolder,
    }
    items.push( tempFile )
  });

  return items.sort((a, b) => {
    return b.dateModified - a.dateModified;
  });

}

export async function getOneDriveFiles(searchKey: string) {
  // const search = searchKey ? searchKey : ".xlsx";
  let files = [];
  let mainDriveId = null;
  // let mainDrive = null;

  try {
    // const userId = store.main.userMe.id;
    // get my drives
    // let drives = await globalGraphClient.api("/users/"+userId+"/drives")
    //   .get();
    // mainDrive = drives.value[1];

    // get my drives
    let myDrive = await globalGraphClient.api("/me/drive/root")
      .get();
    mainDriveId = myDrive.parentReference.driveId;

    const tempFiles = await globalGraphClient
      // .api("/drives/"+mainDrive.id+"/root/search(q='"+search+"')")
      .api("/drives/"+mainDriveId+"/root/children")
      // .api("/me/drive/root/children")
      // .api("me/drive/root/search(q='"+search+"')")
      // .select("name,webUrl,size,lastModifiedDateTime,id")
      // .expand('thumbnails')
      // .filter("startswith(name, 'lic')")
      // .orderby('lastModifiedDateTime DESC')
      .get();

      files = tempFiles.value.filter((obj: any) => {
      return obj.name.toLowerCase().includes(".xlsx");
    });
  } catch (err) {
    console.log("ERROR getOneDriveFiles", err);
  }
  // set the fileUrl
  let finalFiles = [];
  files.forEach((file) => {
    let driveItemInfo = {
      driveItemId: file.id,
      driveItemType: "",
      fileName: file.name,
      fileUrl: "/drives/"+mainDriveId+"/items/"+file.id,
      fileWebUrl: file.webUrl,
      shareType: "owner",
      creatorEmail: file.createdBy.user.email,
    }
    let tempFile = { ...file, driveItemInfo };
    tempFile.type = "document";
    tempFile.lastModified = new Date(tempFile.lastModifiedDateTime);
    tempFile.createdDateTime = new Date(tempFile.createdDateTime);
    finalFiles.push( tempFile )
  });
  return finalFiles.sort((a, b) => {
    return b.lastModified - a.lastModified;
  });
}

export async function getSharePointFiles(searchKey: string) {
  let files = [];
  let mainDriveId = null;

  try {
    // get my drives
    let myDrive = await globalGraphClient.api("/sites/root/drive")
      .get();
      mainDriveId = myDrive.parentReference.driveId;

    const tempFiles = await globalGraphClient
      .api("/drives/"+mainDriveId+"/root/children")
      .get();

      files = tempFiles.value.filter((obj: any) => {
      return obj.name.toLowerCase().includes(".xlsx");
    });
  } catch (err) {
    console.log("ERROR getOneDriveFiles", err);
  }
  // set the fileUrl
  let finalFiles = [];
  files.forEach((file) => {
    let driveItemInfo = {
      driveItemId: file.id,
      driveItemType: "",
      fileName: file.name,
      fileUrl: "/drives/"+mainDriveId+"/items/"+file.id,
      fileWebUrl: file.webUrl,
      shareType: "owner",
      creatorEmail: file.createdBy.user.email,
    }
    let tempFile = { ...file, driveItemInfo };
    tempFile.type = "document";
    tempFile.lastModified = new Date(tempFile.lastModifiedDateTime);
    tempFile.createdDateTime = new Date(tempFile.createdDateTime);
    finalFiles.push( tempFile )
  });
  return finalFiles.sort((a, b) => {
    return b.lastModified - a.lastModified;
  });
}

export async function getOneDriveRecentFiles(searchKey: string) {
  let files = [];

  try {
    const tempFiles = await globalGraphClient
      .api("/me/drive/recent")
      .get();

      files = tempFiles.value.filter((obj: any) => {
      return obj.name.toLowerCase().includes(".xlsx");
    });
  } catch (err) {
    console.log("ERROR getOneDriveRecentFiles", err);
  }
  // set the fileUrl
  let finalFiles = [];
  files.forEach((file) => {
    let driveItemInfo = {
      driveItemId: file.id,
      driveItemType: "",
      fileName: file.name,
      fileUrl: "/drives/"+file.remoteItem.parentReference.driveId+"/items/"+file.id,
      fileWebUrl: file.webUrl,
      shareType: "owner",
      creatorEmail: file.createdBy.user.email,
    }
    let tempFile = { ...file, driveItemInfo };
    tempFile.type = "document";
    tempFile.lastModified = new Date(tempFile.lastModifiedDateTime);
    tempFile.createdDateTime = new Date(tempFile.createdDateTime);
    finalFiles.push( tempFile )
  });
  return finalFiles
}

export async function searchOneDriveFiles(search) {
  if (!search) return [];

  let files = [];
  try {
    let requests = [];
    requests.push({
      entityTypes: [ "driveItem" ],
      query: {
        "queryString": search+" AND filetype:xlsx AND isDocument=true"
      }
    })
    const $options = { requests: requests };

    const searchResults = await globalGraphClient
      .api("/search/query")
      .post($options);

    if (searchResults?.value.length > 0) {
      const hitsContainers = searchResults.value[0].hitsContainers;
      const hits = hitsContainers[0].hits;
      hits.forEach((hit) => {
        files.push({
          id: hit.resource.id,
          driveId: hit.resource.parentReference.driveId,
          name: hit.resource.name,
          size: hit.resource.size,
          webUrl: hit.resource.webUrl,
          lastModifiedDateTime: hit.resource.lastModifiedDateTime
        });
      })
    }
  } catch (err) {
    console.log("ERROR searchOneDriveFiles", err);
  }
  // set the fileUrl
  let finalFiles = [];
  files.forEach((file) => {
    let driveItemInfo = {
      driveItemId: file.id,
      driveItemType: "",
      fileName: file.name,
      fileUrl: "/drives/"+file.driveId+"/items/"+file.id,
      fileWebUrl: file.webUrl,
      shareType: "",
      // creatorEmail: file.createdBy.user.email,
    }
    let tempFile = { ...file, driveItemInfo };
    tempFile.lastModified = new Date(tempFile.lastModifiedDateTime);
    tempFile.type = "document";
    finalFiles.push( tempFile )
  });
  return finalFiles.sort((a, b) => {
    return b.lastModified - a.lastModified;
  });
}

function getSharingUrlFromWebUrl(webUrl) {
  // get the encoded url
  let encoded = "u!"+window.btoa(webUrl).replaceAll("=", "").replaceAll("/", "_").replaceAll("+", "-");
  return encoded;
}

export async function getDriveItemInfoFromWebUrl(webUrl) {
  let encodedUrl = getSharingUrlFromWebUrl(webUrl);
  try {
    let graphUrl = "/shares/"+encodedUrl+"/driveItem";
    let driveItem = await globalGraphClient
        .api(graphUrl)
        .get();
    // build the info object
    let driveItemInfo = {
      driveItemId: driveItem.id,
      driveItemType: "",
      fileName: driveItem.name,
      fileUrl:
        "/drives/" + driveItem.parentReference?.driveId + "/items/" + driveItem.id,
      fileWebUrl: driveItem.webUrl,
      shareType: "owner",
      creatorEmail: driveItem.createdBy?.user?.email,
    };
    return driveItemInfo;
  } catch (error) {
    console.log("ERROR getDriveItemInfoFromWebUrl", error);
    return null;
  }
}

export async function getDriveLinkForDriveItem(
  driveItemInfo: driveItemInfoType,
  readOnly: boolean = false,
  anonymousLink: boolean = false)
{
  let shareFileUrl = undefined;
  let shareGlobalLink = undefined;
  let webUrl = undefined;
  let fileUrl = driveItemInfo.fileUrl;
  try {
    // get the share link
    const permission = {
      type: readOnly ? "view" : "edit",
      scope: anonymousLink ? "anonymous" : "organization"
    };
    let shareLink = await globalGraphClient
      .api(fileUrl + "/createLink")
      .post(permission);
    // get the encoded url
    webUrl = shareLink.link.webUrl;
    let encoded = getSharingUrlFromWebUrl(shareLink.link.webUrl);
    // get the shared driveItem
    shareGlobalLink = "/shares/"+encoded+"/driveItem";
    let sharedDriveItem = await globalGraphClient
      .api(shareGlobalLink)
      .get();
    // get the fileUrl of this driveItem
    shareFileUrl = "/drives/"+sharedDriveItem.parentReference.driveId+"/items/"+sharedDriveItem.id;

  } catch (err) {
    console.log("ERROR getDriveLinkForDriveItem", err);
  }
  return { shareFileUrl, webUrl };
}

export async function checkPermissionToWebUrlDriveItem(accessInfo: any)
{
  // let webUrl = accessInfo.driveItemInfo.webUrl;
  // webUrl = "https://airrange-my.sharepoint.com/:x:/g/personal/jonis_airrange_onmicrosoft_com/EU6_5iWxzS9PncvlDi0G08wBm0kW24sOIKU8M3iAmb09pg";
  // try {
  //   let encoded = getSharingUrlFromWebUrl(webUrl);
  //   // get the shared driveItem
  //   const permission = {
  //     recipients: [
  //       {
  //         email: 'leeg@airrange.onmicrosoft.com',
  //       }
  //     ],
  //     roles: ['write']
  //   };
  //   let shareGlobalLink = "/shares/"+encoded+"/permission/grant";
  //   let sharedDriveItem = await globalGraphClient
  //     .api(shareGlobalLink)
  //     .post(permission);
  //   // get the fileUrl of this driveItem
  //   //shareFileUrl = "/drives/"+sharedDriveItem.parentReference.driveId+"/items/"+sharedDriveItem.id;

  // } catch (err) {
  //   console.log("ERROR getDriveLinkForDriveItem", err);
  // }
}

export async function getOneDriveSharedWithMeFiles() {
  const sharedWithMeUrl = store.main.optionAllowExternalData ? "me/drive/sharedWithMe?allowexternal=true" : "me/drive/sharedWithMe"

  const tempFiles = await globalGraphClient
    .api(sharedWithMeUrl)
    .select("name,webUrl,size,lastModifiedDateTime,id,remoteItem")
    // .filter("startswith(name, 'lic')")
    .orderby('lastModifiedDateTime DESC')
    .get();

  let files = tempFiles.value.filter((obj: any) => {
    return obj.name.includes(".xlsx");
  });
  // set the fileUrl
  let finalFiles = [];
  files.forEach((file) => {
    // get a share url from fileUrl
    const sharingUrl = getSharingUrlFromWebUrl(file.webUrl);

    let driveItemInfo = {
      driveItemId: file.id,
      driveItemType: file.remoteItem.parentReference.driveType,
      fileId: file.id,
      fileName: file.remoteItem.name,
      fileUrl: "/drives/"+file.remoteItem.parentReference.driveId+"/items/"+file.id,
      sharingUrl: "/shares/"+sharingUrl,
      tenantId: file.remoteItem.sharepointIds.tenantId,
      siteId: file.remoteItem.sharepointIds.siteId,
      siteUrl: file.remoteItem.sharepointIds.siteUrl,
      listId: file.remoteItem.sharepointIds.listId,
      webId: file.remoteItem.sharepointIds.webId,
      webUrl: file.webUrl,
      remoteWebUrl: file.remoteItem.webUrl,
      shareType: "shared"
    }
    let tempFile = { ...file, driveItemInfo };
    tempFile.type = "document";
    finalFiles.push( tempFile )
  });

  return finalFiles;
}

export async function uploadFileToOneDrive(file, fileName = "upload.xlsx", options="") {
  let newFileName = fileName;
  try {
    // is there already such a file
    const tempFiles = await globalGraphClient
    .api("/me/drive/root/children")
    .get();

    let doubletCount = getFileInFileListCount(tempFiles.value, newFileName);
    if (doubletCount > 0) {
      var i = 0;
      do {
        i += 1;
        if (i > 100) return;
        newFileName = fileName.replace(".xlsx", "-"+i+".xlsx");
        doubletCount = getFileInFileListCount(tempFiles.value, newFileName);
      } while (doubletCount > 0);
    }

    const stream = file;
    const res = await globalGraphClient.api("/me/drive/root:/"+newFileName+":/content")
      .put(stream);

    if (options === "noautorun") return;
    // run this file
    const driveItemInfo = store.workbooks.getDriveItemInfoForFileRes(res);
    // open this file
    driveItemInfo && store.workbooks.openDriveItemWorkbook(driveItemInfo);
  } catch (err) {
    console.log("ERROR uploadFileToOneDrive", err);
  }
}

export async function uploadFileToOneDriveFromUrl(fileUrl, fileName = "upload.xlsx", callback, options = "") {
  // download the file
  fetch(fileUrl)
  .then(res => res.blob()) // Gets the response and returns it as a blob
  .then(blob => {
    // Here's where you get access to the blob
    // And you can use it for whatever you want
    // Like calling ref().put(blob)
    uploadFileToOneDrive(blob, fileName, options);
    if (callback) callback(fileName);
    if (!callback) return;
  });
}

export async function getDriveItemVersions(driveItemInfo: driveItemInfoType)
{
  let fileUrl = driveItemInfo.fileUrl;
  let versions = [];
  try {
    let tempVersions = await globalGraphClient
      .api(fileUrl + "/versions")
      .get();

    if (tempVersions) {
      tempVersions.value?.forEach((version) => {
        // sheet name
        versions.push({
          id: version.id,
          lastModifiedDateTime: version.lastModifiedDateTime,
          lastModifiedBy: version.lastModifiedBy?.user?.email,
          dateStr: version.lastModifiedDateTime ? String(version.lastModifiedDateTime).substr(0, 10) : "",
          size: version.size
        });
      })
    };
  } catch (err) {
    console.log("ERROR getDriveItemVersions", err);
  }
  return versions;
}

export async function getPermissionsForDriveItem(driveItemInfo: driveItemInfoType) {
  let fileUrl = driveItemInfo.fileUrl;
  let permissionResult;
  let permissions;

  try {
    // get the share link
    permissionResult = await globalGraphClient
      .api(fileUrl +'/permissions')
      .get();
    permissions = await getPermissionsOutOfPermissionResult(permissionResult?.value);
    // console.log("permissions", permissions);
  } catch (err) {
    console.log("ERROR getPermissionsForDriveItem", err);
  }
  return permissions;
}

async function getPermissionsOutOfPermissionResult(permissionValue) {
  let userList = [];

  permissionValue.forEach((value, index) => {
    const roles = value.roles || [];
    const link = value.link;
    const hasPassword = value.hasPassword;
    // identities
    if (value.grantedToIdentities) {
      value.grantedToIdentities.forEach((identity, index) => {
        const user = identity.user;
        if (user) {
          // read the user entry
          userList.push({
            type: "user",
            permissionId: value.id,
            userId: user.id,
            userName: user.displayName,
            userEmail: user.email,
            roles: roles,
            link_preventsDownload: link?.preventsDownload,
            link_scope: link?.scope,
            link_type: link?.type,
            webUrl: link?.webUrl,
            hasPassword: hasPassword,
            sortIndex: 10
          })
        }
      })
    }
    // grantedTo
    if (value.grantedTo?.user) {
      const user = value.grantedTo.user;
      // read the user entry
      userList.push({
        type: roles.includes("owner") ? "owner" : "user",
        permissionId: value.id,
        userId: user.id,
        userName: user.displayName,
        userEmail: user.email,
        webUrl: null, //driveItemInfo.fileUrl,
        roles: roles,
        sortIndex: 1
      })
    }
    // links
    if (!value.grantedTo) { //&& !value.grantedToIdentities) {
      userList.push({
        type: "link",
        permissionId: value.id,
        roles: roles,
        userName: link?.scope === "organization" ? "organization wide link" : "anonymous link",
        link_preventsDownload: link?.preventsDownload,
        link_scope: link?.scope,
        link_type: link?.type,
        webUrl: link?.webUrl,
        hasPassword: hasPassword,
        sortIndex: 99
      })
    }
  })
  // sort by sortIndex
  userList.sort((a, b) => (a.sortIndex > b.sortIndex) ? 1 : -1);
  return userList;
}

export async function addPermissionsForDriveItem(driveItemInfo: driveItemInfoType, email, role) {
  let fileUrl = driveItemInfo.fileUrl;
  let permissionRole = role;

  //==========================
  // INVITE
  //==========================

  const permission = {
    recipients: [
      {
        email: email
      }
    ],
    message: "Here's the file that we're collaborating on.",
    requireSignIn: true,
    sendInvitation: false,
    roles: [ permissionRole ],
    // password: "123",
    // expirationDateTime: "2021-02-15"
  };
  let res = undefined;
  try {
    await globalGraphClient.api(fileUrl+'/invite')
      .post(permission);
  } catch (err) {
    console.log("ERROR addPermissionsForDriveItem", err)
  }

  return res;
}

export async function addShareLinkForDriveItem(driveItemInfo: driveItemInfoType, role ) {
  let fileUrl = driveItemInfo.fileUrl;
  let permissionRole = role ? role : "edit";

  //==========================
  // LINK
  //==========================

  const permissionLink = {
    type: permissionRole,
    // password: "ThisIsMyPrivatePassword",
    scope: "organization"
  };
  let resLink = undefined;
  try {
    resLink = await globalGraphClient.api(fileUrl + '/createLink')
      .post(permissionLink);
  } catch (err) {
    console.log("ERROR addShareLinkForDriveItem", err)
  }

  return resLink;
}

export async function removePermissionsForDriveItem(driveItemInfo: driveItemInfoType, permissionId) {
  let fileUrl = driveItemInfo.fileUrl;

  // const permission = await globalGraphClient.api(fileUrl + '/permissions/' + permissionId)
  //   .get();

  await globalGraphClient.api(fileUrl + '/permissions/' + permissionId)
    .delete();
}

// async function fileUpload(elem) {
// 	let file = elem.files[0];
// 	try {
// 		let response = await largeFileUpload(client, file, file.name);
// 		console.log(response);
// 		console.log("File Uploaded Successfully.!!");
// 	} catch (error) {
// 		console.error(error);
// 	}
// }

// async function largeFileUpload(client, file) {
//   try {
//       let options = {
//           path: "/desired upload path",
//           fileName: file.name,
//           rangeSize: 1024 * 1024,
//       };
//       const uploadTask = await globalGraphClient.OneDriveLargeFileUploadTask.create(client, file, options);
//       const response = await uploadTask.upload();
//       return response;
//   } catch (err) {
//     console.log("ERROR largeFileUpload", err);
//   }
// }

// export async function grantFilePermissionForDriveItem(encodedFileUrl: string, recipientEmails: string[], readOnly: boolean) {
//   if (!encodedFileUrl) return;
//   if (!recipientEmails) return;
//   // prepare options
//   let recipients: recipientEmailType[] = [];
//   recipientEmails.forEach(recipientEmail => {
//     recipients.push({ email: recipientEmail })
//   })
//   const permission = {
//     recipients: recipients,
//     roles: [readOnly ? "read" : "write"]
//   };
//   // call the api
//   try {
//     let res = await globalGraphClient.api('/shares/'+encodedFileUrl+'/permission/grant')
//       .post(permission);
//       console.log("TEST - getGrantFilePermissionForDriveItem", res);
//       return res;
//     } catch (err) {
//       console.log("ERROR grantFilePermissionForDriveItem", err);
//       return
//   }
// }

// export async function getFileVersionsForDriveItem(driveItemInfo: driveItemInfoType) {
//   let fileUrl = driveItemInfo.fileUrl;
//   let versions;
//   let analytics;

//   try {
//     // get the version history
//     versions = await globalGraphClient
//       .api(fileUrl +'/versions')
//       .get();
//     // get the analytics
//     analytics = await globalGraphClient
//       .api(fileUrl +'/analytics')
//       .get();
//   } catch (err) {
//     console.log("ERROR getFileVersionsForDriveItem", err);
//   }
//   return {versions, analytics};
// }

// export async function getFilePreviewForDriveItem(driveItemInfo: driveItemInfoType) {
//   let fileUrl = driveItemInfo.fileUrl;
//   let versions;

//   try {
//     // get the share link
//     versions = await globalGraphClient
//       .api(fileUrl +'/preview')
//       .get();
//   } catch (err) {
//     console.log("ERROR getFilePreviewForDriveItem", err);
//   }
//   return versions; //.getUrl, postUrl for embedding
// }

// export async function getShareLinkForDriveItem(
//   driveItemInfo: driveItemInfoType,
//   readOnly: boolean = false,
//   anonymousLink: boolean = false)
// {
//   let shareFileUrl = undefined;
//   let fileUrl = driveItemInfo.fileUrl;
//   try {
//     // get the share link
//     const permission = {
//       type: readOnly ? "view" : "edit",
//       scope: anonymousLink ? "anonymous" : "organization"
//     };
//     let shareLink = await globalGraphClient
//       .api(fileUrl + "/createLink")
//       .post(permission);
//     // get the encoded url
//     let encoded = getSharingUrlFromWebUrl(shareLink.link.webUrl);
//     // get the share fileUrl
//     shareFileUrl = "/shares/"+encoded+"/driveItem";
//   } catch (err) {
//     console.log("ERROR getShareFileUrlForDriveItem", err);
//   }
//   return shareFileUrl;
// }

